import { FLEET_LIVE_STAGES } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type FleetLiveModel,
  itemMachineText,
  itemStateText,
  STAGE_LABEL,
} from '../lib/fleet-live';
import { count } from '../lib/time';

// A Fleet Live canvas's text alternative (#6778): visually hidden, read by assistive tech.
// The work by stage (each stage's name, then its items with their state and machine; no
// count, since the item list is bounded and only #6777's counts are numbers here) and
// the machines (name plus 8-char id, never a hostname, with jobs and CPU, or "CPU not
// reported"). The Topology adds its three node counts.

export function FleetLiveText(props: {
  readonly model: FleetLiveModel;
  readonly nodes?: boolean;
}): JSX.Element {
  const byStage = () =>
    FLEET_LIVE_STAGES.map((stage, i) => ({
      stage,
      items: props.model.items.filter((it) => it.stage === i),
    }));
  return (
    <div class="sr-only">
      <Show when={props.nodes}>
        <p>
          {t('fleet.live.sr_nodes', {
            queued: count(props.model.topology.queued),
            in_ci: count(props.model.topology.inCi),
            shipped: count(props.model.topology.shipped),
          })}
        </p>
      </Show>
      <ul aria-label={t('fleet.live.sr_work')}>
        <For each={byStage()}>
          {(row) => (
            <li>
              {STAGE_LABEL[row.stage]()}
              <Show when={row.items.length > 0}>
                <ul>
                  <For each={row.items}>
                    {(it) => (
                      <li>
                        {t('fleet.live.sr_item', {
                          label: it.label,
                          state: itemStateText(it),
                          machine: itemMachineText(it),
                        })}
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </li>
          )}
        </For>
      </ul>
      <Show when={props.model.machines.length > 0}>
        <ul aria-label={t('fleet.live.sr_machines')}>
          <For each={props.model.machines}>
            {(m) => (
              <li>
                {m.cpu === null
                  ? t('fleet.live.sr_lane_no_cpu', { label: m.label, jobs: m.jobs })
                  : t('fleet.live.sr_lane', {
                      label: m.label,
                      jobs: m.jobs,
                      cpu: Math.round(m.cpu),
                    })}
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}
